import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import TagSelect, { Tag } from "src/reusable/components/select/tag-select";
import {
  useCreateConfig,
  useGetAllAtomAssistTags,
  useGetAllToolkits,
  useUpdateConfig,
} from "../../AssistantServices";
import { AtomAssistConfigTags, PersonaAPIData, Toolkit } from "../../types";

import { zodResolver } from "@hookform/resolvers/zod";
import { PERSONA_NAME_ERROR, isValidPersonaName } from "./personaName";
import { CloseOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useHistory } from "react-router-dom";
import { ADMIN_ONLY_TOOLKITS } from "src/constants";
import { usePermission } from "src/helpers/Auth";
import { useGetUserDetails } from "src/helpers/UserDetails";
import { useIsAdmin } from "src/helpers/useIsAdmin";
import { getShareCoverage } from "src/api/shares";
import ShareDialog, {
  StagedShareList,
  useStagedShares,
} from "src/reusable/components/ShareDialog";
import { NucToast } from "src/reusable/components/Toast";
import ChatFormLabel from "src/reusable/components/ChatFormLabel";
import ModelComboSelect from "src/reusable/components/ModelComboSelect";
import { useMcpServersEnabled } from "src/config/posthog";
import ToolkitSelect from "src/reusable/components/select/toolkit-select";
import { isMcpToolkitName } from "src/views/remote-resources/mcpResource";
import { AtomAssistProductID } from "src/reusable/products";
import DatasetSelectorContent from "src/reusable/Chat/PromptInput/DatasetSelectorContent";
import { z } from "zod";

const toolkitSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
});

const tagSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable().optional(),
});

const personaSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Name is required")
    .refine(isValidPersonaName, PERSONA_NAME_ERROR),
  description: z.string(),
  modelVersionNum: z.number().nullable(),
  modelId: z.number().nullable(),
  datasets: z.array(z.any()),
  tags: z.array(tagSchema),
  toolkits: z.array(toolkitSchema),
  systemPrompt: z.string(),
  initialPrompt: z.string(),
  topSearchResults: z.number().optional(),
  maxToolIterations: z
    .number()
    .min(-1)
    .refine((val) => val !== 0, {
      message:
        "Cannot be 0 (use -1 for unlimited or positive number for limit)",
    }),
  autoRoutable: z.boolean().optional(),
});

type PersonaFormConfig = z.infer<typeof personaSchema>;

interface SuggestedDataset {
  id: number;
  name: string;
}

interface PersonaDialogProps {
  persona?: PersonaAPIData;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  navigateOnCreate?: boolean;
  /** AI-suggested datasets to auto-append and display */
  suggestedDatasets?: SuggestedDataset[];
  /** Whether AI dataset suggestions are still loading */
  isSuggestingDatasets?: boolean;
  /** Override the update button label (e.g. "Configure" for workflow setup) */
  updateLabel?: string;
  /** Called after a successful update (not create) */
  onUpdateSuccess?: () => void;
  /** Called with the created config after a successful create — lets callers
   * chain a follow-up (e.g. the workflow slots panel binds the new agent to
   * the slot it was customized from). */
  onCreateSuccess?: (created: PersonaAPIData) => void;
  /** Opens a share dialog on a just-created agent whose staged grants did not
   *  all land. Pass it when the parent unmounts or rekeys this dialog on
   *  create; without it the dialog opens its own, which only works while it
   *  stays mounted. */
  onShareGrantsFailed?: (created: PersonaAPIData) => void;
}

const PersonaDialog = ({
  persona,
  open,
  setOpen,
  navigateOnCreate = true,
  suggestedDatasets,
  isSuggestingDatasets = false,
  updateLabel,
  onUpdateSuccess,
  onCreateSuccess,
  onShareGrantsFailed,
}: PersonaDialogProps) => {
  const history = useHistory();
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  // Datasets the pending update attaches, checked against the share roster
  // once it saves.
  const addedDatasetIdsRef = useRef<number[]>([]);
  // The agent whose share dialog is open: the one being edited, or a freshly
  // created one whose staged grants did not all apply.
  const [accessTarget, setAccessTarget] = useState<{
    id: number;
    name: string;
  } | null>(null);
  // Grants chosen on the create pane; applied after the agent exists.
  const {
    staged: stagedShares,
    setStaged: setStagedShares,
    applyAfterCreate,
  } = useStagedShares<PersonaAPIData>(
    "agent",
    onShareGrantsFailed ??
      ((created) => setAccessTarget({ id: created.id, name: created.name })),
  );
  const currentUser = useGetUserDetails();
  const canPublishAgents = usePermission("agent:promote").canIt;
  const isAdmin = useIsAdmin();

  const { data: aaTags, isSuccess: isAATagsSuccess } =
    useGetAllAtomAssistTags();

  const { data: toolkitsData } = useGetAllToolkits();

  // MCP toolkits stay out of the picker while the mcp-servers rollout flag
  // is off. A persona that already has one keeps its selected chip (the
  // Autocomplete renders values absent from options), so saving never
  // strips an existing assignment.
  const mcpServersEnabled = useMcpServersEnabled();

  // Filter toolkits based on admin status
  const filteredToolkits = useMemo(() => {
    if (!toolkitsData) return [];
    const visible = mcpServersEnabled
      ? toolkitsData
      : toolkitsData.filter(
          (toolkit: Toolkit) => !isMcpToolkitName(toolkit.name),
        );
    if (isAdmin) return visible;
    return visible.filter(
      (toolkit: Toolkit) => !ADMIN_ONLY_TOOLKITS.includes(toolkit.name),
    );
  }, [toolkitsData, isAdmin, mcpServersEnabled]);

  const queryParams = new URLSearchParams(window.location.search);
  const deleteModelLink = queryParams.get("delete-model-link");

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors, isDirty },
    reset,
  } = useForm<PersonaFormConfig>({
    resolver: zodResolver(personaSchema),
    defaultValues: {
      name: persona?.name ?? "",
      description: persona?.description ?? "",
      modelVersionNum: persona?.model_version?.version_number ?? null,
      modelId: persona?.config_data?.model_id ?? null,
      datasets: persona?.datasets ?? [],
      tags: persona?.atom_assist_config_tags?.map((tag) => tag.tag) ?? [],
      toolkits: persona?.config_toolkits ?? [],
      systemPrompt: persona?.system_prompt ?? "",
      initialPrompt: persona?.initial_prompt ?? "",
      topSearchResults: persona?.config_data?.top_search_results ?? 3,
      maxToolIterations: persona?.config_data?.max_tool_iterations ?? 5,
      autoRoutable: persona?.auto_routable ?? false,
    },
  });

  const isUpdateAction = !!persona?.slug;
  const isCreateAction = !persona?.slug;
  const isSeeded = persona?.seeded ?? false;
  // Server flags when present; an older backend omits my_access.
  const canManageAccess = persona?.my_access
    ? persona.my_access.can_manage
    : isAdmin || (!!currentUser && persona?.create_user_fk === currentUser.id);

  const {
    mutate: createConfig,
    data: createConfigData,
    isLoading: createConfigLoading,
    isSuccess: createConfigSuccess,
    isError: isCreateConfigError,
    error: createConfigError,
  } = useCreateConfig();

  const {
    mutate: updateConfig,
    isLoading: updateConfigLoading,
    isSuccess: updateConfigSuccess,
    isError: isUpdateConfigError,
    error: updateConfigError,
  } = useUpdateConfig(persona?.slug);

  const handleClose = () => {
    setConfirmCloseOpen(false);
    setOpen(false);
  };

  const handleCloseRequest = () => {
    if (isDirty) {
      setConfirmCloseOpen(true);
    } else {
      handleClose();
    }
  };

  const onSubmit: SubmitHandler<PersonaFormConfig> = (data) => {
    const payload = {
      name: data.name,
      description: data.description,
      tags: data.tags,
      model_version_fk: data.modelVersionNum,
      system_prompt: data.systemPrompt,
      initial_prompt: data.initialPrompt,
      dataset_ids: data.datasets.map((dataset) => dataset.id),
      config_toolkits: data.toolkits,
      config_data: {
        top_search_results: data.topSearchResults,
        model_id: data.modelId,
        tool_calling: true,
        max_tool_iterations: data.maxToolIterations,
      },
    };

    if (persona?.slug) {
      // clear query params from url if the slug is the same as the deleteModelLink
      if (deleteModelLink) {
        // clear query params
        const url = new URL(window.location.href);
        url.searchParams.delete("delete-model-link");
        window.history.replaceState({}, "", url);
      }
      const before = new Set((persona.datasets ?? []).map((ds) => ds.id));
      addedDatasetIdsRef.current = payload.dataset_ids.filter(
        (id: number) => !before.has(id),
      );
      updateConfig({ ...payload, auto_routable: data.autoRoutable } as any);
    } else {
      createConfig({ ...payload, auto_routable: data.autoRoutable } as any);
    }
  };

  useEffect(() => {
    if (isAATagsSuccess && aaTags) {
      setAvailableTags(aaTags.map((tag: AtomAssistConfigTags) => tag.tag));
    }
  }, [isAATagsSuccess, aaTags]);

  useEffect(() => {
    if (updateConfigSuccess || createConfigSuccess) {
      handleClose();
      if (updateConfigSuccess && onUpdateSuccess) {
        onUpdateSuccess();
      }
      if (updateConfigSuccess && persona) {
        const added = addedDatasetIdsRef.current;
        addedDatasetIdsRef.current = [];
        if (added.length > 0 && canManageAccess) {
          void warnAddedDatasetGaps(persona, added);
        }
      }
      if (createConfigSuccess && onCreateSuccess) {
        onCreateSuccess(createConfigData);
      }
      if (createConfigSuccess && createConfigData?.id) {
        void finishCreate(createConfigData);
      }
    }
    // handleClose is redeclared per render (not memoized); listing it would
    // re-fire this effect every render while a success flag is true.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    updateConfigSuccess,
    createConfigSuccess,
    createConfigData,
    onUpdateSuccess,
    onCreateSuccess,
  ]);

  // Navigation waits for the staged grants and is skipped when one failed,
  // so the share dialog that opens to retry is not navigated away from.
  const finishCreate = async (created: PersonaAPIData) => {
    const applied = await applyAfterCreate(created);
    if (applied && navigateOnCreate) {
      history.push(`/assistant?persona=${created.slug}`);
    }
  };

  // Attaching a dataset to a shared agent does not share the dataset: say so
  // when someone on the roster cannot read what was just added. Advisory, so
  // a failed coverage read is silent.
  const warnAddedDatasetGaps = async (
    agent: PersonaAPIData,
    addedIds: number[],
  ) => {
    let coverage;
    try {
      coverage = await getShareCoverage("agent", agent.id);
    } catch {
      return;
    }
    const gaps = coverage.datasets.filter(
      (row) =>
        row.dataset_id !== null &&
        addedIds.includes(row.dataset_id) &&
        row.readable_count < coverage.people_total,
    );
    if (gaps.length === 0) return;
    NucToast({
      message: `Some people "${agent.name}" is shared with can't read ${gaps
        .map((row) => row.name)
        .join(", ")}. Open Manage access on the agent to share ${
        gaps.length === 1 ? "it" : "them"
      }.`,
      type: "warning",
      autoClose: 8000,
      closeButton: true,
    });
  };

  useEffect(() => {
    if (open) {
      reset({
        name: persona?.name ?? "",
        description: persona?.description ?? "",
        modelVersionNum: persona?.model_version?.version_number ?? null,
        modelId: persona?.config_data?.model_id ?? null,
        datasets: persona?.datasets ?? [],
        tags: persona?.atom_assist_config_tags?.map((tag) => tag.tag) ?? [],
        toolkits: persona?.config_toolkits ?? [],
        systemPrompt: persona?.system_prompt ?? "",
        initialPrompt: persona?.initial_prompt ?? "",
        topSearchResults: persona?.config_data?.top_search_results ?? 3,
        maxToolIterations: persona?.config_data?.max_tool_iterations ?? 5,
        autoRoutable: persona?.auto_routable ?? false,
      });
    }
  }, [open, persona, reset]);

  useEffect(() => {
    if (!open) {
      setConfirmCloseOpen(false);
      // A canceled create must not carry its recipients into the next one.
      setStagedShares([]);
    }
  }, [open]);

  // Auto-append AI-suggested datasets when dialog opens
  useEffect(() => {
    if (open && suggestedDatasets && suggestedDatasets.length > 0) {
      const currentDatasets: any[] = getValues("datasets") ?? [];
      const existingIds = new Set(currentDatasets.map((ds: any) => ds.id));
      const newSuggestions = suggestedDatasets
        .filter((ds) => !existingIds.has(ds.id))
        .map((ds) => ({ id: ds.id, name: ds.name }));
      if (newSuggestions.length > 0) {
        setValue("datasets", [...currentDatasets, ...newSuggestions]);
      }
    }
  }, [open, suggestedDatasets, getValues, setValue]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseRequest}
        fullWidth
        maxWidth="md"
        PaperProps={{
          component: "form",
          onSubmit: handleSubmit(onSubmit),
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%", pr: 1 }}
        >
          <DialogTitle fontWeight="bold">
            {persona?.slug ? "Edit Agent" : "Create Agent"}
          </DialogTitle>
          <IconButton onClick={handleCloseRequest}>
            <CloseOutlined />
          </IconButton>
        </Stack>

        {isUpdateConfigError && (
          <DialogContent sx={{ pt: 1 }}>
            <Alert severity="error">
              {Array.isArray((updateConfigError as any)?.response?.data?.detail)
                ? (updateConfigError as any).response.data.detail.join(", ")
                : ((updateConfigError as any)?.response?.data?.detail ??
                  "An error occurred while updating the agent")}
            </Alert>
          </DialogContent>
        )}

        {isCreateConfigError && (
          <DialogContent sx={{ pt: 1 }}>
            <Alert severity="error">
              {Array.isArray((createConfigError as any)?.response?.data?.detail)
                ? (createConfigError as any).response.data.detail.join(", ")
                : ((createConfigError as any)?.response?.data?.detail ??
                  "An error occurred while creating the agent")}
            </Alert>
          </DialogContent>
        )}

        <DialogContent
          dividers={true}
          sx={{
            pt: 1,
            "&::-webkit-scrollbar": {
              width: "0.8em",
            },
            "&::-webkit-scrollbar-track": {
              outline: "1px solid rgba(0,0,0,.1)",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,.1)",
              borderRadius: "10px",
            },
          }}
        >
          {isSeeded && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This is a system agent. Only datasets, routing, and default
              settings can be changed. Use &quot;Make a copy&quot; to create an
              editable version.
            </Alert>
          )}
          <Stack flexDirection="column" spacing={3}>
            {/* Required Fields */}
            <Box
              sx={{
                p: 2.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                backgroundColor: "background.paper",
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight="medium"
                sx={{ mb: 0.5 }}
              >
                Basic Information
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 2, display: "block" }}
              >
                Required fields
              </Typography>
              <Stack spacing={2.5}>
                <Box>
                  <ChatFormLabel label="Agent Name" />
                  <TextField
                    {...register("name")}
                    id="config-name"
                    name="name"
                    placeholder="Give the agent a name"
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={isSeeded}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                </Box>
                <Box>
                  <ChatFormLabel label="Description (Optional)" />
                  <TextField
                    {...register("description")}
                    id="config-description"
                    name="description"
                    placeholder="Describe the agent's purpose and capabilities..."
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    disabled={isSeeded}
                  />
                </Box>
                <Box>
                  <ChatFormLabel label="Tags (Optional)" />
                  <FormHelperText sx={{ mb: 1 }}>
                    Add tags to categorize and organize your agent
                  </FormHelperText>
                  <Controller
                    name="tags"
                    control={control}
                    render={({ field }) => (
                      <TagSelect
                        value={field.value}
                        onChange={(newTags) => {
                          field.onChange(newTags);
                        }}
                        availableTags={availableTags}
                        setAvailableTags={setAvailableTags}
                        placeholder="Add tags..."
                        error={!!errors.tags}
                        errorMessage={errors.tags?.message as string}
                        allowCreation={true}
                        disabled={isSeeded}
                      />
                    )}
                  />
                </Box>
              </Stack>
            </Box>

            {/* Model & Resources */}
            <Box
              sx={{
                p: 2.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                backgroundColor: "background.paper",
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight="medium"
                sx={{ mb: 0.5 }}
              >
                Model & Resources
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 2, display: "block" }}
              >
                Optional - defaults will be used if not specified
              </Typography>
              <Stack spacing={2.5}>
                <Box>
                  <Controller
                    name="modelId"
                    control={control}
                    render={({ field }) => (
                      <ModelComboSelect
                        label="Model (Optional)"
                        helperText="Leave empty to use the platform default model"
                        value={field.value}
                        onChange={(selected) => {
                          if (selected?.value) {
                            setValue("modelId", selected.value.id, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                            setValue(
                              "modelVersionNum",
                              selected.value.versions.slice(-1)[0]
                                .version_number,
                              { shouldDirty: true },
                            );
                          } else {
                            setValue("modelId", null, { shouldDirty: true });
                            setValue("modelVersionNum", null, {
                              shouldDirty: true,
                            });
                          }
                        }}
                        onlyDeployed={true}
                        onlyAgentBindable={true}
                        isClearable
                        productId={AtomAssistProductID}
                        isDisabled={isSeeded}
                        error={
                          Boolean(deleteModelLink) &&
                          persona?.slug === deleteModelLink &&
                          field.value === persona?.config_data?.model_id
                        }
                        errorMessage="Please Select a Different Model"
                      />
                    )}
                  />
                </Box>
                <Box>
                  {isSuggestingDatasets && (
                    <Chip
                      icon={
                        <CircularProgress size={14} sx={{ color: "inherit" }} />
                      }
                      label="Finding relevant datasets..."
                      color="info"
                      variant="outlined"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                  )}
                  {!isSuggestingDatasets &&
                    suggestedDatasets &&
                    suggestedDatasets.length > 0 && (
                      <Chip
                        icon={<AutoAwesomeIcon />}
                        label={`${suggestedDatasets.length} dataset${suggestedDatasets.length > 1 ? "s" : ""} suggested by AI`}
                        color="info"
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                  <ChatFormLabel label="Default Dataset Scope (Optional)" />
                  <FormHelperText sx={{ mb: 1 }}>
                    Leave empty to let the agent search every dataset you have
                    access to. Selecting datasets scopes its searches to just
                    those by default.
                  </FormHelperText>
                  {/* Datasets are saved as a snapshot of dataset_ids. Future: persist a live dataset_folder link on the persona — see issue #1444. */}
                  <Controller
                    name="datasets"
                    control={control}
                    render={({ field }) => (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        <DatasetSelectorContent
                          selectedDatasets={field.value ?? []}
                          onSelectionChange={field.onChange}
                          showChips
                        />
                      </Box>
                    )}
                  />
                </Box>
                <Box>
                  <ToolkitSelect
                    name="toolkits"
                    control={control}
                    options={filteredToolkits}
                    label="Toolkits (Optional)"
                    helperText="Select toolkits to give the agent additional capabilities"
                    error={!!errors.toolkits}
                    errorMessage={errors.toolkits?.message as string}
                    disabled={isSeeded}
                  />
                </Box>
              </Stack>
            </Box>

            {/* Behavior & Prompts */}
            <Box
              sx={{
                p: 2.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                backgroundColor: "background.paper",
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight="medium"
                sx={{ mb: 0.5 }}
              >
                Behavior & Prompts
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 2, display: "block" }}
              >
                Configure how the agent responds
              </Typography>
              <Stack spacing={2.5}>
                <Box>
                  <ChatFormLabel label="Greeting Message (Optional)" />
                  <FormHelperText sx={{ mb: 1 }}>
                    Custom greeting when starting a conversation
                  </FormHelperText>
                  <TextField
                    {...register("initialPrompt")}
                    id="initial-prompt"
                    name="initialPrompt"
                    placeholder="e.g., Hello! I'm here to help you with..."
                    multiline
                    variant="outlined"
                    size="small"
                    fullWidth
                    minRows={2}
                    disabled={isSeeded}
                  />
                </Box>
                <Box>
                  <ChatFormLabel label="Custom Instructions (Optional)" />
                  <FormHelperText sx={{ mb: 1 }}>
                    Specific instructions that guide the agent's behavior
                  </FormHelperText>
                  <TextField
                    {...register("systemPrompt")}
                    id="system-prompt"
                    name="systemPrompt"
                    placeholder="e.g., Always provide detailed technical explanations with relevant industry standards..."
                    multiline
                    variant="outlined"
                    size="small"
                    fullWidth
                    minRows={3}
                    disabled={isSeeded}
                  />
                </Box>
              </Stack>
            </Box>

            {/* Advanced Options */}
            <Box
              sx={{
                p: 2.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                backgroundColor: "background.paper",
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight="medium"
                sx={{ mb: 0.5 }}
              >
                Advanced Options
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 2, display: "block" }}
              >
                Fine-tune advanced settings
              </Typography>
              <Stack spacing={2.5}>
                <Box>
                  <ChatFormLabel label="Max Tool Iterations (Optional)" />
                  <FormHelperText sx={{ mb: 1 }}>
                    Maximum consecutive tool calls. Defaults to 5. Lower values
                    (3-5) prevent excessive API usage. Set to -1 for unlimited.
                  </FormHelperText>
                  <Controller
                    name="maxToolIterations"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        id="max-tool-iterations"
                        placeholder="5"
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled={isSeeded}
                        inputProps={{ min: -1, step: 1 }}
                        error={!!errors.maxToolIterations}
                        helperText={errors.maxToolIterations?.message}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === "" ? 5 : Number(e.target.value),
                          )
                        }
                      />
                    )}
                  />
                </Box>
                {isCreateAction && (
                  <Box>
                    <ChatFormLabel label="Who can access" />
                    <StagedShareList
                      open={open}
                      value={stagedShares}
                      onChange={setStagedShares}
                      tid="agent-dialog"
                      note="Only you can see this agent until you share it."
                    />
                  </Box>
                )}
                {isUpdateAction && (
                  <Box>
                    <ChatFormLabel label="Intelligent Routing" />
                    <FormHelperText sx={{ mb: 1 }}>
                      {isAdmin
                        ? "When enabled, this agent can be automatically selected by the routing system"
                        : "Only admins can change routing settings"}
                    </FormHelperText>
                    <Controller
                      name="autoRoutable"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.value ?? false}
                              onChange={field.onChange}
                              disabled={!isAdmin}
                            />
                          }
                          label={
                            field.value
                              ? "Included in routing"
                              : "Not included in routing"
                          }
                        />
                      )}
                    />
                  </Box>
                )}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 1 }}>
          {isUpdateAction && canManageAccess && (
            <Button
              size="large"
              variant="outlined"
              onClick={() =>
                setAccessTarget({ id: persona.id, name: persona.name })
              }
              data-testid="agent-dialog-manage-access"
              sx={{ mr: "auto" }}
            >
              Manage access
            </Button>
          )}
          <Button
            disableElevation
            size="large"
            variant="contained"
            color="inherit"
            onClick={handleCloseRequest}
            data-testid="agent-dialog-cancel"
          >
            Cancel
          </Button>
          {isCreateAction && (
            <LoadingButton
              type="submit"
              disableElevation
              variant="contained"
              color="primary"
              size="large"
              loading={createConfigLoading}
              data-testid="agent-dialog-create"
            >
              Create
            </LoadingButton>
          )}
          {isUpdateAction && (
            <LoadingButton
              type="submit"
              disableElevation
              variant="contained"
              color="primary"
              size="large"
              loading={updateConfigLoading}
              data-testid="agent-dialog-update"
            >
              {updateLabel || "Update"}
            </LoadingButton>
          )}
        </DialogActions>
        <Dialog
          open={confirmCloseOpen}
          onClose={() => setConfirmCloseOpen(false)}
          maxWidth="xs"
          aria-labelledby="persona-confirm-discard-title"
          aria-describedby="persona-confirm-discard-description"
        >
          <DialogTitle id="persona-confirm-discard-title" fontWeight="bold">
            Discard unsaved changes?
          </DialogTitle>
          <DialogContent>
            <Typography id="persona-confirm-discard-description">
              You have unsaved changes. Are you sure you want to close?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              autoFocus
              disableElevation
              variant="contained"
              color="inherit"
              onClick={() => setConfirmCloseOpen(false)}
              data-testid="agent-dialog-keep-editing"
            >
              Keep Editing
            </Button>
            <Button
              disableElevation
              variant="contained"
              color="error"
              onClick={handleClose}
              data-testid="agent-dialog-discard"
            >
              Discard
            </Button>
          </DialogActions>
        </Dialog>
      </Dialog>
      {/* Outside the form dialog: it must stay mounted after a create closes
          the form, so a failed staged grant can still open it. */}
      {accessTarget && (
        <ShareDialog
          open
          onClose={() => setAccessTarget(null)}
          resource={{ type: "agent", ...accessTarget }}
          canTransferOwnership={
            isAdmin ||
            (!!currentUser &&
              (persona?.id === accessTarget.id
                ? persona.create_user_fk === currentUser.id
                : true))
          }
          canPublish={canPublishAgents}
        />
      )}
    </>
  );
};

export default PersonaDialog;
